import Logo from '_atoms/Logo';
import Tree from '_atoms/Tree';
import { LanguageAutoSelection } from 'Helper/LanguageAutoSelection';
import { FunctionComponent, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import './BaseLayout.scss';

import { TranslatableString } from '_i18n/no/translations';
import LocalstorageLastUrl from '_services/LocalStorage/Lasturl';
import useLayoutToggler from '_services/Redux/Layout/Selector/useLayoutToggler';
import { RootReduxStateShape } from '_types/Redux';
import CloseButton from 'Components/CloseButton';
import Toast from 'Components/Common/Toast';
import UADetector from 'Helper/UADetector';
import useNavigate from 'Hooks/useNavigate';
import Header from 'Pages/Common/Header/Header';
import { useSelector } from 'react-redux';
type PropsType = {
    children: React.JSX.Element;
    classes?: string;
    /** page is fullscreen, with no scrolling.
     * Disables css to allow scrolling 100px beyond element's height */
    isFullScreen?: boolean;
    showLogo?: boolean;
    showTree?: boolean;
    showBackButton?: boolean;
    backButtonIcon?: string;
    backOverride?: string | (() => void);
    showClose?: boolean;
    hasAutoScrollToTop?: boolean;
    hasStickyHeader?: boolean;
    backButtonClass?: string;
    stickyHeaderWithScrollEffect?: boolean;
    secondaryButton?: React.JSX.Element;
    title?: ReactNode | TranslatableString;
};

const BaseLayout: FunctionComponent<PropsType> = ({
    classes,
    children,
    isFullScreen = false,
    backOverride = '',
    showLogo = true,
    showTree = false,
    showBackButton = true,
    showClose = false,
    backButtonIcon,
    hasAutoScrollToTop = true,
    hasStickyHeader = false,
    stickyHeaderWithScrollEffect = false,
    title,
    secondaryButton,
}): React.JSX.Element => {
    const setClasses = typeof classes === 'string' ? classes : '';
    let allClasses = 'base-layout-container ' + setClasses;
    const { isIPhone } = UADetector();
    const { i18n } = useTranslation();
    const location = useLocation();
    const { displayOverviewTip } = useLayoutToggler();
    const { toast } = useSelector((state: RootReduxStateShape) => state.toast);
    const navigate = useNavigate();

    if (hasStickyHeader || stickyHeaderWithScrollEffect) {
        allClasses = allClasses + ' ' + 'sticky-header';
    }

    const { state: historyState } = window.history;

    useEffect(() => {
        displayOverviewTip(true);
    }, [location]);

    /**
     * Scroll to top when
     * component render
     */
    useEffect(() => {
        if (historyState) {
            const { y: top } = historyState;
            if (top) {
                requestAnimationFrame(() => window.scrollTo(0, top));
                return;
            }
        }

        if (hasAutoScrollToTop) {
            window.scrollTo(0, 0);
        }
    }, [location.pathname, hasAutoScrollToTop]);

    useEffect(() => {
        /**
         * Call language auto selector to select language based on url parma "language"
         */
        LanguageAutoSelection(i18n);
    }, [i18n]);

    // removing back btn logic which we used for stripe
    useEffect(() => {
        const previousUrl = document.referrer;
        const internationalStoreUrl =
            process.env.REACT_APP_SHOP_INTERNATIONAL_URL;
        const norwayStoreUrl = process.env.REACT_APP_SHOP_NORWAY_URL;

        if (
            previousUrl &&
            ((internationalStoreUrl && // if REACT_APP_SHOP_INTERNATIONAL_URL present in env and previous url also contains this
                previousUrl.includes(internationalStoreUrl)) ||
                (norwayStoreUrl && previousUrl.includes(norwayStoreUrl))) &&
            history?.state?.idx === 0
        ) {
            LocalstorageLastUrl.add(previousUrl);
        }
    }, []);

    /**
     * Handles going back either to backOverwrite,
     * or the previous in history
     */
    const goBack = () => {
        /**
         * check if typeof backOverride is function then
         * call the back function directly
         */
        if (typeof backOverride === 'function') {
            backOverride();
            return;
        }
        if (backOverride) {
            navigate(backOverride, { shouldAwaitConfirmation: true });
            return;
        }

        // when back from stripe
        const urlsForBack = LocalstorageLastUrl.get();
        const previousUrl = document.referrer;
        if (
            history?.state?.idx === 0 &&
            urlsForBack.length &&
            previousUrl.includes('stripe.com') // when back from stripe
        ) {
            if (urlsForBack[1]) {
                history.pushState(null, document.title, urlsForBack[1]);
            }

            if (urlsForBack[0].includes('http')) {
                window.location.href = urlsForBack[0];
                return;
            }

            navigate(urlsForBack[0], { shouldAwaitConfirmation: true });

            LocalstorageLastUrl.remove();
            return;
        }

        navigate(-1, { shouldAwaitConfirmation: true });
    };

    useEffect(() => {
        if (isFullScreen) {
            document.body.classList.add('full-screen');
        } else {
            document.body.classList.remove('full-screen');
        }
    }, [isFullScreen]);

    return (
        <div className={allClasses}>
            {isIPhone && <div className="ios-statusbar-hack"></div>}

            <Header
                title={title}
                showBackButton={showBackButton}
                stickyHeaderWithScrollEffect={stickyHeaderWithScrollEffect}
                backButtonIcon={backButtonIcon}
                secondaryButton={secondaryButton}
                goBack={goBack}
            />
            {showClose && (
                <div className="base-layout-header-close-button">
                    <CloseButton onClose={goBack} />
                </div>
            )}
            {showLogo ? <Logo /> : ''}
            {showTree && <Tree />}
            {children}
            <Toast {...toast} />
        </div>
    );
};

export default BaseLayout;
